import { standardQuerySerializer } from '../api/serializers';
import {
  applyQueryConfig,
  makeCancelable,
  responseStringTransformer,
  ServiceHandler,
} from '../utils';
import { agencyServiceMappings } from './agencyServiceMappings';

import { AgencyHierarchyResponseDto } from '../types/agencies/dto/agency-heirarchy.dto';

const agencyParamsSerializer = (params: any) =>
  standardQuerySerializer(applyQueryConfig(agencyServiceMappings, params));

const fetchDAAgenciesHandler = makeCancelable(
  ServiceHandler({
    url: 'api/account/v1/agency/search',
    method: 'get',
    paramsSerializer: agencyParamsSerializer,
  })
);
export const fetchDAAgencies = (params: Record<string, unknown> = {}) =>
  fetchDAAgenciesHandler({ params });

const fetchAgenciesHandler = makeCancelable(
  ServiceHandler({
    url: 'api/account/v1/agency/search',
    paramsSerializer: agencyParamsSerializer,
  })
);
export const fetchAgencies = (params: Record<string, unknown> = {}) =>
  fetchAgenciesHandler({ params });

/**
 * The Retail/Wholesale cross-market count. Hits the same endpoint as
 * {@link fetchAgencies} but is deliberately a SEPARATE makeCancelable
 * instance: makeCancelable keeps one AbortController per wrapped handler, so
 * sharing fetchAgencies would make this count abort the main listing request
 * (surfacing as the listing's "problem loading agency data" toast).
 */
const fetchCrossMarketAgenciesHandler = makeCancelable(
  ServiceHandler({
    url: 'api/account/v1/agency/search',
    paramsSerializer: agencyParamsSerializer,
  })
);
export const fetchCrossMarketAgencies = (
  params: Record<string, unknown> = {}
) => fetchCrossMarketAgenciesHandler({ params });

const markFavAgencyHandler = ServiceHandler({
  url: 'api/auth/v1/admin/agencies/:agencyId/favorite/:flag',
  method: 'put',
});
export function markFavAgency(agencyId: any, flag: any) {
  return markFavAgencyHandler(agencyId, flag);
}

const fetchAgencyStatsHandler = ServiceHandler({
  url: 'api/account/v1/agency/stats/state',
  paramsSerializer: agencyParamsSerializer,
});
export const fetchAgencyStats = (params: Record<string, unknown> = {}) =>
  fetchAgencyStatsHandler({ params });

const uppercaseStateCode = (agency = {}) => {
  // @ts-expect-error TS(2339): Property 'state' does not exist on type '{}'.
  return { ...agency, state: agency.state?.toUpperCase() };
};

const fetchAgencyDetailsHandler = ServiceHandler({
  url: '/api/account/v1/agency/:agencyId',
});
export function fetchAgencyDetails(agencyId: any) {
  return fetchAgencyDetailsHandler(agencyId, {
    transformResponse: [responseStringTransformer, uppercaseStateCode],
  });
}

const agencyDetailsHandler = ServiceHandler({ url: '/api/account/v1/agency' });
export function agencyDetails() {
  return agencyDetailsHandler();
}

export const getAggregators = ServiceHandler({
  url: '/api/account/v1/agency/daids',
});

const addNewAgencyHandler = ServiceHandler({
  url: '/api/auth/v1/admin/agencies',
  method: 'post',
});
export function addNewAgency(agency: any) {
  return addNewAgencyHandler({ data: agency });
}

const updateAgencyHandler = ServiceHandler({
  url: 'api/auth/v1/admin/agencies/:accountId',
  method: 'put',
});
export function updateAgency(accountId: any, contactDetails: any) {
  return updateAgencyHandler(accountId, { data: contactDetails });
}

const addAgentToAggregatorHandler = ServiceHandler({
  url: 'api/account/v1/da/agency/:daId/:agencyId',
  method: 'put',
});
export function addAgentToAggregator(daId: any, agencyId: any) {
  return addAgentToAggregatorHandler(daId, agencyId);
}

const addAgentToClusterHandler = ServiceHandler({
  url: 'api/account/v1/cluster/agency/:id/:agencyId',
  method: 'put',
});
export function addAgentToCluster(id: any, agencyId: any) {
  return addAgentToClusterHandler(id, agencyId);
}

const addAgenciesToClusterHandler = ServiceHandler({
  url: 'api/account/v1/cluster/:clusterId/agencies',
  method: 'put',
});
export function addAgenciesToCluster(clusterId: string, agencyIds: string[]) {
  return addAgenciesToClusterHandler(clusterId, { data: agencyIds });
}

const updateAgentHandler = ServiceHandler({
  url: 'api/account/v1/account/contact',
  method: 'put',
});
export function updateAgent(agency: any) {
  return updateAgentHandler({ data: agency });
}

const getAgentInfoHandler = ServiceHandler({
  url: 'api/account/v1/agency/contact/:agencyId',
});
export function getAgentInfo(agencyId: any) {
  return getAgentInfoHandler(agencyId);
}

const refreshAgencyFirmographicDataHandler = ServiceHandler({
  url: '/api/account/v1/agency/reload/:accountId',
});
export function refreshAgencyFirmographicData(accountId: any) {
  return refreshAgencyFirmographicDataHandler(accountId);
}

export const getAgencyDetails = makeCancelable(
  ServiceHandler({ url: '/api/account/v1/agency' })
);

const setAgencyDetailsHandler = makeCancelable(
  ServiceHandler({ url: '/api/account/v1/agency/profile', method: 'put' })
);
export const setAgencyDetails = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => setAgencyDetailsHandler({ data });

const getRetailAgentLicenseHandler = makeCancelable(
  ServiceHandler({
    url: '/api/agency/v1/agent/license/retail/search',
    paramsSerializer: agencyParamsSerializer,
  })
);
export const getRetailAgentLicense = (params: Record<string, unknown> = {}) =>
  getRetailAgentLicenseHandler({ params });

const setRetailAgentLicenseHandler = makeCancelable(
  ServiceHandler({ url: '/api/agency/v1/agent/license/retail', method: 'put' })
);
export const setRetailAgentLicense = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => setRetailAgentLicenseHandler({ data });

export const getAgencySegments = ServiceHandler({
  url: '/api/agency/v1/segments',
});

export const getAgencyHierarchy = ServiceHandler<AgencyHierarchyResponseDto>({
  url: '/api/account/v1/agency/hierarchy',
});

export const getFullAgencyHierarchyAsFlatList = ServiceHandler({
  url: '/api/account/v1/agency/hierarchy/agencies',
});

export const getAgencyAccountById = ServiceHandler({
  url: '/api/account/v1/agency/:id',
});

export const getAgencyPrograms = ServiceHandler({
  url: '/api/account/v1/agency/programs/:agencyId',
});

export const postAgencyProgram = ServiceHandler({
  url: '/api/account/v1/agency/programs/:agencyId',
  method: 'POST',
});

export const putAgencyProgram = ServiceHandler({
  url: '/api/account/v1/agency/programs/:agencyId/:programId',
  method: 'PUT',
});

export const deleteAgencyProgram = ServiceHandler({
  url: '/api/account/v1/agency/programs/:agencyId/:programId',
  method: 'DELETE',
});

const createAgencyAppointmentHandler = ServiceHandler({
  url: '/api/account/v1/appointments',
  method: 'POST',
});
export const createAgencyAppointment = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => createAgencyAppointmentHandler({ data });

export const getAgencyAppointments = ServiceHandler({
  url: '/api/account/v1/appointments/agency/:agencyId',
});

export const deleteAgencyAppointment = ServiceHandler({
  url: '/api/account/v1/appointments/:appointmentId',
  method: 'DELETE',
});

export const updateAgencyAppointment = ServiceHandler({
  url: '/api/account/v1/appointments/:appointmentId',
  method: 'PUT',
});

export const getAppointerKeys = ServiceHandler({
  url: '/api/account/v1/appointments/appointer-keys',
});
